import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  useCurrentAccount,
  useSignPersonalMessage,
  useDisconnectWallet,
} from "@mysten/dapp-kit";
import type { OperatorObject } from "@altheia-xyz/types";
import { api } from "@/lib/api";

/**
 * Sui-side auth, separate from the Solana AltheiaAuthProvider so neither can
 * break the other. Flow: dapp-kit wallet (Slush/Suiet) → /auth/challenge →
 * signPersonalMessage(challenge) → /auth/verify (chain=sui) → JWT. Keyed by the
 * Sui address. Session persisted under its own storage key.
 */

interface SuiAuthState {
  suiAddress: string | null;
  token: string | null;
  operator: OperatorObject | null;
  status: "hydrating" | "idle" | "signing" | "ready" | "error";
  error: string | null;
  signInSui: () => Promise<void>;
  signOut: () => void;
}

const SuiAuthContext = createContext<SuiAuthState | null>(null);
const STORAGE_KEY = "altheia.sui.session.v1";

interface PersistedSession {
  token: string;
  address: string;
  exp: number;
}

function readJwtExp(token: string): number | null {
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(atob(parts[1]!.replace(/-/g, "+").replace(/_/g, "/"))) as { exp?: number };
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

function loadPersisted(): PersistedSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedSession;
    if (!parsed.token || !parsed.address) return null;
    if (parsed.exp && parsed.exp <= Math.floor(Date.now() / 1000)) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function persist(s: PersistedSession): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* localStorage disabled — degrade */
  }
}

function clearPersisted(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* same */
  }
}

export function SuiAuthProvider({ children }: { children: ReactNode }) {
  const account = useCurrentAccount();
  const { mutateAsync: signPersonalMessage } = useSignPersonalMessage();
  const { mutate: disconnect } = useDisconnectWallet();

  const [suiAddress, setSuiAddress] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [operator, setOperator] = useState<OperatorObject | null>(null);
  const [status, setStatus] = useState<SuiAuthState["status"]>("hydrating");
  const [error, setError] = useState<string | null>(null);

  // Hydrate from localStorage; optimistic restore + background verify.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const persisted = loadPersisted();
      if (!persisted) {
        if (!cancelled) setStatus("idle");
        return;
      }
      setToken(persisted.token);
      setSuiAddress(persisted.address);
      setStatus("ready");
      try {
        const fresh = await api.getOperatorMe(persisted.token);
        if (!cancelled) setOperator(fresh);
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        if (msg.startsWith("401") || msg.startsWith("403")) {
          clearPersisted();
          setToken(null);
          setOperator(null);
          setSuiAddress(null);
          setStatus("idle");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const signInSui = useCallback(async () => {
    if (!account) {
      setError("connect a Sui wallet first");
      return;
    }
    setStatus("signing");
    setError(null);
    try {
      const wallet = account.address;
      const { challenge } = await api.authChallenge(wallet);
      const messageBytes = new TextEncoder().encode(challenge);
      const { signature } = await signPersonalMessage({ message: messageBytes });
      const verified = await api.authVerifySui({ wallet, challenge, signature });
      setToken(verified.token);
      setOperator(verified.operator);
      setSuiAddress(wallet);
      setStatus("ready");
      const exp = readJwtExp(verified.token) ?? Math.floor(Date.now() / 1000) + 86_400;
      persist({ token: verified.token, address: wallet, exp });
    } catch (e) {
      setStatus("error");
      setError(e instanceof Error ? e.message : "sui sign-in failed");
    }
  }, [account, signPersonalMessage]);

  const signOut = useCallback(() => {
    setToken(null);
    setOperator(null);
    setSuiAddress(null);
    setStatus("idle");
    setError(null);
    clearPersisted();
    disconnect();
  }, [disconnect]);

  return (
    <SuiAuthContext.Provider
      value={{ suiAddress, token, operator, status, error, signInSui, signOut }}
    >
      {children}
    </SuiAuthContext.Provider>
  );
}

export function useSuiAuth(): SuiAuthState {
  const ctx = useContext(SuiAuthContext);
  if (!ctx) throw new Error("useSuiAuth must be used within SuiAuthProvider");
  return ctx;
}
